<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-header-left">
        <router-link to="/client/inventory/purchase-orders" class="back-btn">
          <i class="ri-arrow-left-line"></i>
        </router-link>
        <h1 class="page-title">進貨單詳情</h1>
      </div>
      <span v-if="order" :class="['badge', statusClass(order.status)]" style="font-size:14px">
        {{ statusLabel(order.status) }}
      </span>
    </div>

    <div v-if="!order && loading" class="loading-state">載入中...</div>
    <div v-else-if="!order" class="loading-state text-muted">找不到進貨單</div>

    <template v-if="order">
      <div class="info-card">
        <div class="info-grid">
          <div class="info-item"><span class="info-label">單號</span><code>{{ order.orderNo }}</code></div>
          <div class="info-item"><span class="info-label">入庫倉庫</span>{{ order.warehouseName }}</div>
          <div class="info-item"><span class="info-label">供應商</span>{{ order.supplierName || '-' }}</div>
          <div class="info-item"><span class="info-label">供應商聯絡</span>{{ order.supplierContact || '-' }}</div>
          <div class="info-item"><span class="info-label">預計到貨日</span>{{ order.expectedDate ? order.expectedDate.slice(0,10) : '-' }}</div>
          <div class="info-item"><span class="info-label">採購總金額</span>{{ formatCurrency(order.totalAmount) }}</div>
          <div class="info-item"><span class="info-label">備註</span>{{ order.notes || '-' }}</div>
          <div class="info-item">
            <span class="info-label">建立者</span>
            <span v-if="order.createdByName">
              {{ order.createdByName }}
              <span class="text-muted" style="font-size:12px">（{{ order.createdByUsername }}）</span>
            </span>
            <span v-else>-</span>
          </div>
          <div class="info-item"><span class="info-label">建立時間</span>{{ formatDate(order.createdAt) }}</div>
        </div>
      </div>

      <!-- 操作按鈕區 -->
      <div class="action-bar">
        <!-- draft → 確認進貨單 -->
        <button v-if="order.status === 'draft'" class="btn btn-primary" :disabled="confirming" @click="confirmOrder">
          <span v-if="confirming"><i class="ri-loader-4-line spin"></i> 確認中...</span>
          <span v-else><i class="ri-checkbox-circle-line"></i> 確認進貨單</span>
        </button>
        <!-- confirmed / partially_received → 入庫 -->
        <button v-if="canReceive" class="btn btn-success" @click="openReceiveModal">
          <i class="ri-archive-drawer-line"></i> 執行入庫
        </button>
        <!-- draft → 取消 -->
        <button v-if="order.status === 'draft'" class="btn btn-danger-outline" :disabled="cancelling" @click="cancelOrder">
          <span v-if="cancelling">取消中...</span>
          <span v-else><i class="ri-close-circle-line"></i> 取消進貨單</span>
        </button>
      </div>

      <div class="form-card">
        <div class="section-header">
          <h3 class="section-title">商品明細</h3>
        </div>

        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>商品名稱</th>
                <th>規格 SKU</th>
                <th>採購數量</th>
                <th>已入庫</th>
                <th>進度</th>
                <th>採購單價</th>
                <th>小計</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in order.items" :key="item.id">
                <td>{{ item.productName }}</td>
                <td><code>{{ item.variantSku }}</code></td>
                <td>{{ item.orderedQuantity }}</td>
                <td>{{ item.receivedQuantity }}</td>
                <td>
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: progressPct(item) + '%' }"></div>
                  </div>
                  <span class="progress-label">{{ progressPct(item) }}%</span>
                </td>
                <td>{{ formatCurrency(item.unitCost) }}</td>
                <td>{{ formatCurrency(item.orderedQuantity * item.unitCost) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- 確認入庫 Modal -->
    <div v-if="showReceiveModal" class="modal-overlay" @click.self="showReceiveModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>確認入庫</h3>
          <button class="modal-close" @click="showReceiveModal = false"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <p class="modal-tip">請填寫各品項本次實際入庫數量（數量為 0 代表本次不入庫）</p>
          <div v-for="item in receiveItems" :key="item.itemId" class="receive-item">
            <div class="receive-item-name">
              {{ item.productName }}
              <span class="receive-item-sku">{{ item.variantSku }}</span>
            </div>
            <div class="receive-item-meta">
              採購 {{ item.orderedQuantity }} 件 ／ 已入庫 {{ item.receivedQuantity }} 件 ／ 剩餘 {{ item.orderedQuantity - item.receivedQuantity }} 件
            </div>
            <div class="receive-item-input">
              <label>本次入庫數量：</label>
              <input v-model.number="item.qty" type="number" :min="0" :max="item.orderedQuantity - item.receivedQuantity" class="form-control input-sm" />
            </div>
            <div class="receive-item-input">
              <label>備註：</label>
              <input v-model="item.note" class="form-control" placeholder="選填" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showReceiveModal = false">取消</button>
          <button class="btn btn-primary" :disabled="receiving" @click="submitReceive">
            {{ receiving ? '確認中...' : '確認入庫' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { purchaseOrderService } from '@/api/modules/warehouses.js';



const route = useRoute();
const router = useRouter();
const order = ref(null);
const loading = ref(false);
const confirming = ref(false);
const cancelling = ref(false);
const showReceiveModal = ref(false);
const receiveItems = ref([]);
const receiving = ref(false);

const STATUS_LABELS = { draft: '草稿', confirmed: '已確認', partially_received: '部分入庫', completed: '已完成', cancelled: '已取消' };
const STATUS_CLASSES = { draft: 'badge-secondary', confirmed: 'badge-primary', partially_received: 'badge-warning', completed: 'badge-success', cancelled: 'badge-danger' };

const statusLabel = (s) => STATUS_LABELS[s] || s;
const statusClass = (s) => STATUS_CLASSES[s] || 'badge-secondary';
const formatCurrency = (v) => `$${Number(v || 0).toLocaleString()}`;
const formatDate = (d) => d ? new Date(d).toLocaleString('zh-TW') : '-';
const progressPct = (item) => item.orderedQuantity > 0
  ? Math.min(100, Math.round(item.receivedQuantity / item.orderedQuantity * 100))
  : 0;

const canReceive = computed(() => order.value && ['confirmed', 'partially_received'].includes(order.value.status));

async function fetchOrder() {
  loading.value = true;
  try {
    const res = await purchaseOrderService.getPurchaseOrderById(route.params.id);
    order.value = res.data || null;
  } catch (e) { console.error(e); } finally { loading.value = false; }
}

async function confirmOrder() {
  if (!confirm('確認後進貨單將進入待入庫狀態，確定要確認此進貨單嗎？')) return;
  confirming.value = true;
  try {
    await purchaseOrderService.confirmPurchaseOrder(route.params.id);
    await fetchOrder();
  } catch (e) {
    alert(e.response?.data?.message || '確認失敗');
  } finally { confirming.value = false; }
}

async function cancelOrder() {
  if (!confirm('確定要取消此進貨單嗎？此操作無法復原。')) return;
  cancelling.value = true;
  try {
    await purchaseOrderService.cancelPurchaseOrder(route.params.id);
    router.push('/client/inventory/purchase-orders');
  } catch (e) {
    alert(e.response?.data?.message || '取消失敗');
  } finally { cancelling.value = false; }
}

function openReceiveModal() {
  const pending = order.value.items.filter(i => i.receivedQuantity < i.orderedQuantity);
  if (!pending.length) {
    alert('所有品項已全數入庫完成');
    return;
  }
  receiveItems.value = pending.map(i => ({
    itemId: i.id,
    productName: i.productName,
    variantSku: i.variantSku,
    orderedQuantity: i.orderedQuantity,
    receivedQuantity: i.receivedQuantity,
    qty: i.orderedQuantity - i.receivedQuantity,
    note: ''
  }));
  showReceiveModal.value = true;
}

async function submitReceive() {
  const items = receiveItems.value
    .filter(i => i.qty > 0)
    .map(i => ({ itemId: i.itemId, receivedQuantity: i.qty, note: i.note || undefined }));
  if (!items.length) { alert('請輸入至少一筆入庫數量'); return; }

  receiving.value = true;
  try {
    await purchaseOrderService.receivePurchaseOrder(route.params.id, items);
    showReceiveModal.value = false;
    fetchOrder();
  } catch (e) {
    alert(e.response?.data?.message || '入庫失敗');
  } finally { receiving.value = false; }
}

onMounted(fetchOrder);
</script>

<style scoped>
.page-container { padding: 20px; max-width: 900px; }
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.page-header-left { display: flex; align-items: center; gap: 10px; }
.back-btn { color: #666; font-size: 20px; text-decoration: none; }
.page-title { font-size: 22px; font-weight: 600; margin: 0; }
.info-card, .form-card { background: #fff; border: 1px solid #e8e8e8; border-radius: 10px; padding: 20px; margin-bottom: 16px; }
.info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.info-item { display: flex; flex-direction: column; gap: 4px; }
.info-label { font-size: 12px; color: #999; }
.section-title { font-size: 15px; font-weight: 600; margin: 0; color: #333; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
th { background: #f8f9fa; font-weight: 600; color: #555; }
.progress-bar { width: 80px; height: 6px; background: #eee; border-radius: 3px; display: inline-block; vertical-align: middle; margin-right: 6px; }
.progress-fill { height: 100%; background: #27ae60; border-radius: 3px; }
.progress-label { font-size: 12px; color: #666; }
.loading-state { text-align: center; padding: 40px; color: #999; }
.badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.badge-primary { background: #e3f0ff; color: #1a73e8; }
.badge-secondary { background: #f0f0f0; color: #666; }
.badge-success { background: #e6f9ee; color: #27ae60; }
.badge-danger { background: #fde8e8; color: #e74c3c; }
.badge-warning { background: #fff8e1; color: #f39c12; }
.action-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; display: inline-flex; align-items: center; gap: 6px; font-weight: 500; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-primary:hover { background: #1558b0; }
.btn-success { background: #27ae60; color: #fff; }
.btn-success:hover { background: #219150; }
.btn-secondary { background: #f0f0f0; color: #333; }
.btn-danger-outline { background: transparent; border: 1px solid #e74c3c; color: #e74c3c; }
.btn-danger-outline:hover { background: #fde8e8; }
.btn-sm { padding: 4px 12px; font-size: 13px; border-radius: 4px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.spin { display: inline-block; animation: spin .6s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-box { background: #fff; border-radius: 12px; width: 520px; max-width: 95vw; max-height: 80vh; display: flex; flex-direction: column; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #eee; }
.modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
.modal-close { background: none; border: none; cursor: pointer; font-size: 20px; color: #999; }
.modal-body { padding: 16px 20px; overflow-y: auto; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 20px; border-top: 1px solid #eee; }
.modal-tip { font-size: 13px; color: #666; margin-bottom: 12px; }
.receive-item { border: 1px solid #eee; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
.receive-item-name { font-weight: 600; font-size: 14px; }
.receive-item-sku { font-weight: 400; color: #999; margin-left: 6px; font-size: 13px; }
.receive-item-meta { font-size: 12px; color: #666; margin: 4px 0 8px; }
.receive-item-input { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 13px; }
.form-control { padding: 6px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; width: 100%; box-sizing: border-box; }
.input-sm { width: 80px; }
</style>
